import { Box } from "@mui/system";
import { Chart, Tooltip, Axis, Polygon, Legend } from "bizcharts";
import { useEffect, useState } from "react";
import { usePapaParse } from "react-papaparse";
import DownloadButton from "../Common/DownloadButton";

const HeatMapChart = (props) => {
  const [data, setData] = useState([]);

  const csv = props.csv;
  const { readString } = usePapaParse();

   const handleReadString = () => {
    readString(csv, {
      complete: (results, file) => {

        // Remove empty rows
        const cleanedData = results.data.filter(
          (row) => Object.values(row).some((value) => value !== "" && value !== null)
        );

        setData(cleanedData);
      },
      download: true,
      header: true,
      dynamicTyping: true,
      error: (error, file) => {
        console.log("Error while parsing:", error, file);
      },
    });
  };



  const Food_Commodity_Order = [
    { name: "Aubergines/eggplants", order: 1 },
    { name: "Bananas", order: 2 },
    { name: "Bovine fat", order: 3 },
    { name: "Broccoli", order: 4 },
    { name: "Chicken eggs", order: 5 },
    { name: "Cultivated fungi", order: 6 },
    { name: "Grapefruits", order: 7 },
    { name: "Melons", order: 8 },
    { name: "Sweet peppers/bell peppers", order: 9 },
    { name: "Table grapes", order: 10 },
    { name: "Virgine Oil", order: 11 },
    { name: "Wheat", order: 12 },
  ];

  const getCommodityOrder = (commodity) => {
    const found = Food_Commodity_Order.find((f) => f.name === commodity);
    if (found) {
      return found.order;
    }
    return 0;
  };

  useEffect(() => {
    handleReadString();
  }, []);

  const xAxis = [];
  let yAxis = [];
  data.forEach((d) => {
    if (yAxis.indexOf(d[props.yAxisName]) < 0) {
      yAxis.push(d[props.yAxisName]);
    }
    if (xAxis.indexOf(d[props.xAxisName]) < 0) {
      xAxis.push(d[props.xAxisName]);
    }
  });

  const transform = [];
  const valueMap = new Map();

  // Populate the map with existing data and log it
  data.forEach((d) => {
    const xIndex = xAxis.indexOf(d[props.xAxisName]);
    const yIndex = yAxis.indexOf(d[props.yAxisName]);
    const value = d[props.valueField] !== undefined && d[props.valueField] !== null ? Math.round(d[props.valueField] * 10) / 10 : 0;
    const commodity = d["Food commodity"];
    const key = `${xIndex}-${yIndex}`;
    valueMap.set(key, { xIndex, yIndex, value, commodity });
  });

  // Generate all possible combinations and set to 0 if missing
  xAxis.forEach((x, xIndex) => {
    yAxis.forEach((y, yIndex) => {
      const key = `${xIndex}-${yIndex}`;
      if (!valueMap.has(key)) {
        // If there's no existing data, push a default value of 0
        transform.push([xIndex, yIndex, 0, ""]);
      } else {
        // If data exists, push the original value
        const entry = valueMap.get(key);
        transform.push([entry.xIndex, entry.yIndex, entry.value, entry.commodity]);
      }
    });
  });

  const source = transform.map((arr) => {
    return {
      commodities: arr[0],
      quantifications: arr[1],
      percent: arr[2],
      commodity: arr[3],
    };
  });

  const scale = {
    commodities: {
      type: "cat",
      values: xAxis,
    },
    quantifications: {
      type: "cat",
      values: yAxis,
    },
    percent: {
      nice: true,
    },
  };

  const [canvas, setCanvas] = useState(undefined);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 500);
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 500);
    }

    window.addEventListener("resize", handleResize);
    return (_) => {
      window.removeEventListener("resize", handleResize);
    };
  });

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        className="heatMap-title"
        alignItems="center"
      >
        <p
          className="titleChart"
          style={{
            fontFamily: "Roboto Condensed, sans-serif",
            width: "100%",
            marginBottom: ".5rem",
          }}
        >
          {props.title}
        </p>
        <DownloadButton
          canvas={canvas}
          fileName={props.title}
          csv={data}
          isRaw
        />
      </Box>
      <Chart
        padding={[15, 0, 60, 110]}
        scale={scale}
        height={350}
        data={source}
        autoFit
        onGetG2Instance={(chartInstance) => {
          setTimeout(() => {
            setCanvas(chartInstance.canvas);
          }, 1000);
        }}
      >
        <Axis
          name={"commodities"}
          tickLine={null}
          label={{
            style: {
              fontFamily: "Roboto Condensed, sans-serif",
              fontWeight: "bolder",
            },
          }}
          grid={{
            alignTick: false,
            line: {
              style: {
                lineWidth: 1,
                lineDash: null,
                stroke: "#f0f0f0",
              },
            },
          }}
        />
        <Axis
          name={"quantifications"}
          title={null}
          label={{
            style: {
              fontFamily: "Roboto Condensed, sans-serif",
            },
          }}
          grid={{
            alignTick: false,
            line: {
              style: {
                lineWidth: 1,
                lineDash: null,
                stroke: "#f0f0f0",
              },
            },
          }}
        />
        <Tooltip
          customContent={(title, content) => {
            if (content[0]) {
              return (
                '<p style=" padding: 10px 0 0 0; margin: 0; "><b style="color:#22294D;font-family:Roboto Condensed;">' +
                content[0].data.commodity +
                "</b></p>" +
                '<li style="list-style: none; padding: 5px 0 10px 0" data-index={index}>' +
                '<span style="background-color:' +
                content[0].color +
                ';width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:8px;"></span>' +
                '<b style="color:#22294D;font-family:Roboto Condensed;font-weight:normal;">' +
                title +
                " Quantified residues per sample: " +
                Math.round(content[0].data.percent * 10) / 10 +
                "%" +
                "</b>" +
                "</li>"
              );
            }
            return "";
          }}
        />
        <Legend />

        <Polygon
          position={"commodities*quantifications"}
          color={["percent", "#D0D1D5-#484D6A-#22294D"]}
          label={[
            "percent",
            (percent) => {
              return {
                offset: -3,
                style: {
                  fill: percent > 20 ? "#fff" : "#22294D",
                  shadowBlur: 2,
                  shadowColor: "rgba(0, 0, 0, .45)",
                  fontFamily: "Roboto Condensed",
                  padding: "5px",
                },
              };
            },
          ]}
          style={{
            lineWidth: 1,
            stroke: "#fff",
          }}
        />
      </Chart>
    </Box>
  );
};

export default HeatMapChart;
